import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import './App.css';

import {addPapyrosPrefix, DEFAULT_LOCALE, DEFAULT_PROGRAMMING_LANGUAGE, DEFAULT_SERVICE_WORKER} from "./Constants";
import {Papyros, PapyrosConfig} from "./Papyros";
import {InputMode} from "./InputManager";
import {BatchInputHandler} from "./input/BatchInputHandler";
import {CodeMirrorEditor} from "./editor/CodeMirrorEditor";
import {
    Box,
    Button,
    Checkbox,
    Flex,
    HStack,
    Select,
    VStack,
    chakra,
    Spacer,
    Spinner,
    Text as CText,
    Center, SimpleGrid
} from "@chakra-ui/react";
import CodeMirror, {ReactCodeMirrorRef} from "@uiw/react-codemirror";
import {python} from "@codemirror/lang-python";
import {EditorView, Panel, showPanel} from "@codemirror/view";
import {Compartment, Extension} from "@codemirror/state";
import {vim} from "@replit/codemirror-vim";
import {LoadingData, RunState} from "./CodeRunner";
import {SyncClient} from "comsync";
import {Backend} from "./Backend";
import {BackendManager} from "./BackendManager";
import {BackendEvent, BackendEventType} from "./BackendEvent";
import {parseData} from "./util/Util";
import {ProgrammingLanguage} from "./ProgrammingLanguage";
import {proxy} from "comlink";
import {autocompletion} from "@codemirror/autocomplete";
import escapeHTML from "escape-html";
import create from "zustand";

const LOCAL_STORAGE_KEYS = {
    code: addPapyrosPrefix("previous-code"),
    input: addPapyrosPrefix("previous-batch-input")
};

function setUpEditor(editor: CodeMirrorEditor, storageKey: string): void {
    const previousValue = window.localStorage.getItem(storageKey);
    if (previousValue) {
        editor.setText(previousValue);
    }
    editor.onChange(
        {
            onChange: (text: string) => {
                window.localStorage.setItem(storageKey, text);
            },
            delay: 0
        }
    );
}

async function startPapyros(): Promise<void> {
    // Retrieve initial locale and programming language from URL
    // This allows easier sharing of Papyros-related links with others
    // While preventing loading an unwanted backend
    const urlParams = new URLSearchParams(window.location.search);
    const language = Papyros.toProgrammingLanguage(urlParams.get("language") ||
        DEFAULT_PROGRAMMING_LANGUAGE)!;
    const locale = urlParams.get("locale") || DEFAULT_LOCALE;
    const config: PapyrosConfig = {
        standAlone: true,
        programmingLanguage: language,
        locale: locale,
        inputMode: InputMode.Batch,
        channelOptions: {
            serviceWorkerName: DEFAULT_SERVICE_WORKER
        }
    };
    const papyros = new Papyros(config);

    let darkMode = false;
    if (window.matchMedia) {
        darkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
        window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", e => {
            papyros.setDarkMode(e.matches);
        });
    }
    papyros.render({
        standAloneOptions: {
            parentElementId: "root"
        },
        darkMode: darkMode
    });
    setUpEditor(papyros.codeRunner.editor, LOCAL_STORAGE_KEYS.code);
    papyros.codeRunner.editor.focus();
    const handler = papyros.codeRunner.inputManager.getInputHandler(InputMode.Batch);
    if (handler instanceof BatchInputHandler) {
        setUpEditor((handler as BatchInputHandler).batchEditor, LOCAL_STORAGE_KEYS.input);
    }

    await papyros.launch();
}

const PanelRef = React.forwardRef<HTMLDivElement>((props, ref) => (
        <Box ref={ref} {...props} />
    )
)

type CodeProblem = {
    statement: string,
    placeholder: string,
    testCases: string
}

const problems: Array<CodeProblem> = [
    {
        statement: `Write a function to reverse a string`,
        placeholder:
`class Solution:
    def reverse_string(self, inp):
        #`,
        testCases: `
def test_solution():
    test_cases = [("foo", "oof"), ("abba", "abba"), ("honk", "knoh")]
    sol = Solution()
    for inp, outp in test_cases:
        sol_out = sol.reverse_string(inp)
        if sol_out != outp:
            print("case failed:")
            print("input: ", inp)
            print("expected: ", outp)
            print("received: ", sol_out)
            return
    print("passed")


test_solution()`
    },
    {
        statement: `Write a function to reverse a string 2nd test case so it seems different`,
        placeholder:
            `class Solution:
    def reverse_string(self, inpp):
        #`,
        testCases: `
def test_solution():
    test_cases = [("foo", "oof"), ("abba", "abba"), ("honk", "knoh")]
    sol = Solution()
    for inp, outp in test_cases:
        sol_out = sol.reverse_string(inp)
        if sol_out != outp:
            print("case failed:")
            print("input: ", inp)
            print("expected: ", outp)
            print("received: ", sol_out)
            return
    print("passed")


test_solution()`
    },
    {
        statement: `Write a function to reverse a string 3nd test case so it seems different`,
        placeholder:
            `class Solution:
    def reverse_string(self, inppp):
        #`,
        testCases: `
def test_solution():
    test_cases = [("foo", "oof"), ("abba", "abba"), ("honk", "knoh")]
    sol = Solution()
    for inp, outp in test_cases:
        sol_out = sol.reverse_string(inp)
        if sol_out != outp:
            print("case failed:")
            print("input: ", inp)
            print("expected: ", outp)
            print("received: ", sol_out)
            return
    print("passed")


test_solution()`
    },
]

interface AppState {
    currProblemIdx: number
    setCurrProblemIdx: (val: number | ((val: number) => number)) => void
}

const useAppStore = create<AppState>()((set, get) => ({
    currProblemIdx: 0,
    setCurrProblemIdx: (val) => set((state) => ({currProblemIdx: typeof val === "number" ? val : val(state.currProblemIdx)})),
}))

function App() {
    const bottomPanelRef = useRef<HTMLDivElement>(document.createElement("div"))
    const topPanelRef = useRef<HTMLDivElement>(document.createElement("div"))
    const createBottomPanel = (view: EditorView): Panel => {
        return {
            dom: bottomPanelRef.current,
        }
    }
    const createTopPanel = (view: EditorView): Panel => {
        return {
            dom: topPanelRef.current,
            top: true
        }
    }
    const createPanelFactory = (ref: React.MutableRefObject<HTMLDivElement>, mount?: () => void): (view: EditorView) => Panel => {
        return (view: EditorView): Panel => {
            return {
                dom: ref.current,
                mount: mount
            }
        }
    }
    const refs = useRef<ReactCodeMirrorRef>({})
    const [editorView, setEditorView] = useState<EditorView | null>(null)
    const [pythonCompartment, setPythonCompartment] = useState<Compartment>(new Compartment())
    const [autocompleteCompartment, setAutocompleteCompartment] = useState<Compartment>(new Compartment())
    const [vimCompartment, setVimCompartment] = useState<Compartment>(new Compartment())
    const extensions = useMemo<Extension[]>(() => [pythonCompartment.of([]), autocompleteCompartment.of([]), showPanel.of(createBottomPanel), showPanel.of(createTopPanel), vimCompartment.of([]),
    EditorView.baseTheme({
        "&": {
            maxHeight: '100%',
            // minHeight: 0,
        },
        ".cm-scroller": {overflow: 'auto'}
    })], [])
    const languagesOptions = [
        {value: ProgrammingLanguage.Python, text: 'Python'},
        // {value: ProgrammingLanguage.JavaScript, text: 'Javascript'},
    ]
    const DEFAULT_LANGUAGE = languagesOptions[0].value
    const DEFAULT_KEYMAP = 'vim'
    const languageMap = new Map<ProgrammingLanguage, [Compartment, Extension]>([[ProgrammingLanguage.Python, [pythonCompartment, python()]]])
    const keymapMap = new Map<string, [Compartment, Extension]>([['vim', [vimCompartment, vim()]]])
    const languageToProgrammingLanguage = new Map<string, ProgrammingLanguage>([['Python', ProgrammingLanguage.Python], ['Javascript', ProgrammingLanguage.JavaScript]])
    const runStateToString = new Map<RunState, string>([[RunState.Running, "Running"], [RunState.Stopping, "Stopping"], [RunState.Loading, "Loading"], [RunState.AwaitingInput, "Awaiting input"], [RunState.Ready, "Ready"]])
    const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_LANGUAGE)
    const [selectedKeymap, setSelectedKeymap] = useState(DEFAULT_KEYMAP)
    const [runState, setRunStateHook] = useState(RunState.Loading)
    const [runStateText, setRunStateText] = useState(runStateToString.get(runState)!)
    const [previousRunState, setPreviousRunState] = useState(RunState.Ready)
    const [shouldShowStateSpinner, setShouldShowStateSpinner] = useState(false)
    const [runStartTime, setRunStartTime] = useState(new Date().getTime())

    const [backendState, setBackendState] = useState(Promise.resolve({} as SyncClient<Backend>))

    const [outputText, setOutputText] = useState<Array<JSX.Element>>([])

    const codeProblems = problems
    const totalProblems = codeProblems.length - 1
    // const [currProblemIdx, setCurrProblemIdx] = useState(0)
    const currProblemIdx = useAppStore((state) => state.currProblemIdx)
    const setCurrProblemIdx = useAppStore((state) => state.setCurrProblemIdx)

    const isValidProblemIdx = (idx: Number): boolean => {
        return (idx <= totalProblems && idx >= 0)
    }
    const getNextProblem = () => {
        const cpIdx = useAppStore.getState().currProblemIdx
        if (isValidProblemIdx(cpIdx + 1)) {
            setCurrProblemIdx(old => old + 1)
        } else {
            setCurrProblemIdx(0)
        }
    }

    const handleSelectLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const lang = languageToProgrammingLanguage.get(event.target.value)!
        setSelectedLanguage(lang)
    }

    const handleSelectKeymapChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedKeymap(event.target.value)
    }

    const handleAutocompleteCheckmarkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        //
    }

    const runCode = async () => {
        setRunState(RunState.Loading)
        setPreviousRunState(RunState.Loading)
        BackendManager.publish({type: BackendEventType.Start, data: "StartClicked", contentType: "text/plain"})
        let interrupted = false
        let terminated = false
        const backend = await backendState
        const startTime = new Date().getTime()
        try {
            const sleepHack = `import time\ndef sleep(seconds):\n    start=now=time.time()\n    while now-start<seconds:\n        now=time.time()\ntime.sleep=sleep\n`
            const userCodeText = editorView?.state.doc.toString();
            const codeText = sleepHack + userCodeText + problems[currProblemIdx].testCases
            await backend.call(backend.workerProxy.runCode, codeText, undefined)
        } catch (error: any) {
            if (error.type == "InterruptError") {
                interrupted = true
                terminated = true
            } else {
                BackendManager.publish({
                    type: BackendEventType.Error,
                    data: JSON.stringify(error),
                    contentType: "text/json"
                });
                BackendManager.publish({
                    type: BackendEventType.End,
                    data: "RunError", contentType: "text/plain"
                });
            }
        } finally {
            if (runState === RunState.Stopping) {
                interrupted = true
            }
            const elapsedRunTime = (new Date().getTime() - startTime) / 1000;
            setRunState(RunState.Ready, `Code ${interrupted ? "interrupted after" : "executed in"} ${elapsedRunTime} s`)
            if (terminated) {
                await startBackend(editorView!)
            }
        }
    }

    const handleRunCode = (e: React.MouseEvent<HTMLButtonElement>) => {
        runCode()
    }

    const spanify = (text: string, ignoreEmpty = false): JSX.Element => {
        let spanText = text
        if (spanText.includes("\n") && spanText !== "\n") {
            spanText = spanText.split("\n").filter(line => !ignoreEmpty || line.trim().length > 0).join("\n")
        }
        return <chakra.span key={Math.random().toString(36).substr(2, 9)}>{escapeHTML((spanText))}</chakra.span>
    }

    const renderNextElement = (html: JSX.Element, isNewElement = true) => {
        if (isNewElement) {
            setOutputText(oldArr => [...oldArr, html])
        }
    }

    const showOutput = (output: BackendEvent) => {
        const data = parseData(output.data, output.contentType)
        renderNextElement(spanify(data, false))
    }

    const showError = (error: BackendEvent) => {
        console.log(error)
        // const errorData = parseData(error.data, error.contentType)
        // console.log(errorData)
        // setOutputText(errorData)
    }

    const onLoad = (e: BackendEvent) => {
        const loadingData = parseData(e.data, e.contentType) as LoadingData
        // setRunState(previousRunState)
    }

    const onStart = (e: BackendEvent): void => {
        const startData = parseData(e.data, e.contentType) as string
        if (startData.includes("RunCode")) {
            setRunStartTime(new Date().getTime())
            setRunState(RunState.Running)
        }
    }
    const stopCode = async (): Promise<void> => {
        setRunState(RunState.Stopping)
        BackendManager.publish({type: BackendEventType.End, data: "User cancelled run", contentType: "text/plain"})
        const backend = await backendState
        await backend.interrupt()
    }

    const handleStopCode = async (event: React.MouseEvent<HTMLButtonElement>) => {
        stopCode()
    }

    const setRunState = (state: RunState, message?: string) => {
        setRunStateText(message || runStateToString.get(state)!)
        setPreviousRunState(runState)
        setRunStateHook(prev => state)
        setShouldShowStateSpinner(state !== RunState.Ready)
    }

    const startBackend = async (view: EditorView): Promise<void> => {
        setRunState(RunState.Loading)
        const backend = BackendManager.getBackend(selectedLanguage)
        setBackendState(new Promise(async resolve => {
            const workerProxy = backend.workerProxy
            await workerProxy.launch(
                proxy((e: BackendEvent) => BackendManager.publish(e)),
                proxy(() => {
                })) // overflow handler if output is large
            view.dispatch({
                effects: autocompleteCompartment.reconfigure(autocompletion({
                    override: [async context => {
                        const completionContext = Backend.convertCompletionContext(context)
                        return await workerProxy.autocomplete(completionContext)
                    }]
                }))
            })
            return resolve(backend)
        }))

        setRunState(RunState.Ready)
        view.focus()
    }

    const resetOnStart = (e: BackendEvent) => {
        setOutputText([])
    }

    const setCode = (code: string, view: EditorView) => {
        const currCode = view.state.doc.toString();
        view.dispatch({ changes: {from: 0, to: currCode.length, insert: code}})
    }

    const processProblemOutput = (output: BackendEvent, view: EditorView) => {
        const data = output.data as string
        const cpIdx = useAppStore.getState().currProblemIdx
        if (data.includes("pass")) {
            getNextProblem()
            setCode(codeProblems[currProblemIdx].placeholder, view)
        } else {
            setCode(codeProblems[currProblemIdx].placeholder, view)
        }
    }

    const editorCallbackRef = useCallback((node: ReactCodeMirrorRef | null) => {
        if (node !== null) {
            let view = node.view
            if (view) {
                setEditorView(view)
                let [lang, langExt] = languageMap.get(DEFAULT_LANGUAGE)!
                view.dispatch({effects: lang.reconfigure(langExt)})
                let [keymap, keymapExt] = keymapMap.get(DEFAULT_KEYMAP)!
                view.dispatch({effects: keymap.reconfigure(keymapExt)})

                BackendManager.subscribe(BackendEventType.Output, e => showOutput(e))
                BackendManager.subscribe(BackendEventType.Output, e => processProblemOutput(e, view!))
                BackendManager.subscribe(BackendEventType.Error, e => showError(e))

                BackendManager.subscribe(BackendEventType.Loading, e => onLoad(e))
                BackendManager.subscribe(BackendEventType.Start, e => onStart(e))
                BackendManager.subscribe(BackendEventType.Start, e => {resetOnStart(e)})


                startBackend(view)

                setRunState(RunState.Ready)
            }
        }
    }, [])

    return (
        <Box height={'100vh'} width={'100%'}>
            <SimpleGrid columns={2} minH={'300px'} maxH={'100em'} border={'1px'}>
                <Box
                    // whiteSpace={"pre-line"}
                    // verticalAlign={'top'}
                    height={'100em'}
                    width={'50em'}
                    overflow={'auto'}
                >
                    {codeProblems[currProblemIdx].statement}
                </Box>
                <VStack height={'100em'} width={'50em'} minH={0} minW={0} overflow={"auto"}>
                    <Box ref={topPanelRef}>
                        <HStack spacing={'40px'} direction={['column', 'row']}>
                            <Flex>
                                <Select value={selectedLanguage} onChange={handleSelectLanguageChange}>
                                    {languagesOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.text}
                                        </option>
                                    ))}
                                </Select>
                            </Flex>
                            <Flex>
                                <Select value={selectedKeymap} onChange={handleSelectKeymapChange}>
                                    {Array.from(keymapMap, ([kmComp, kmExt]) => ({kmComp, kmExt})).map(option => (
                                        <option key={option.kmComp} value={option.kmComp}>
                                            {option.kmComp}
                                        </option>
                                    ))}
                                </Select>
                            </Flex>
                            <Checkbox onChange={handleAutocompleteCheckmarkChange}>Autocomplete</Checkbox>
                        </HStack>
                    </Box>
                    <CodeMirror
                        ref={editorCallbackRef}
                        height={"100em"}
                        width={"50em"}
                        // minHeight={'0'}
                        // minWidth={'0'}
                        // value={`print("hello world")`}
                        // value={`import time\ntime.sleep(100)`}
                        value={codeProblems[currProblemIdx].placeholder}
                        extensions={extensions}
                    />
                    <Box ref={bottomPanelRef}>
                        <Flex>
                            <Box>
                                {runState === RunState.Ready
                                    ? <Button onClick={handleRunCode}>Run code</Button>
                                    : <Button onClick={handleStopCode}>Cancel</Button>
                                }
                            </Box>
                            <Spacer />
                            <Box>{shouldShowStateSpinner && <Spinner />}</Box>
                            <Center><CText>{runStateText}</CText></Center>
                        </Flex>
                    </Box>
                    <Box
                        whiteSpace={"pre-line"}
                        fontFamily={"MonoLisa monospace"}
                        border={'1px'}
                        borderColor={'gray.200'}
                        width={'50em'}
                        height={'100px'}
                    >{outputText}</Box>
                </VStack>
            </SimpleGrid>
        </Box>
    )
}

export default App;
